import { API_BASE_URL } from '@synergy/config';
import {
    BookmarkBorderOutlined,
    DeleteOutline,
    EditOutlined,
    StarBorderOutlined,
    StarOutlined,
} from '@mui/icons-material';
import { Button, Divider, ListItemText, Menu, MenuItem, TextField } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { paloIsRefused } from '@synergy/components-palo';
import { request } from '@synergy/api';
import { tokens } from '@synergy/theme-palo';
import { useLocation, useNavigate } from 'react-router-dom';

interface SavedView {
    id: number;
    listKey: string;
    name: string;
    query: string;
}

const ENDPOINT = `${API_BASE_URL}/user-list-views`;
const COLUMNS_PARAM = 'columns';
/**
 * Columns the user asked to see although the list hides them by default.
 *
 * It takes a list of its own: "columns" names what was switched off, and a
 * column that starts off has to be able to record that it was switched on —
 * otherwise showing it lasts until the next visit (#2221 R-136).
 */
const SHOWN_PARAM = 'columnsOn';
const ORDER_PARAM = 'columnOrder';

/**
 * The name the list keeps its own last state under.
 *
 * "Was der Benutzer einstellt, bleibt" is a rule of the frame, and a view is
 * already a query under a name stored per user and per list — so the state the
 * user left behind is one more of those, under a name no one can type. It is
 * kept out of the menu and out of the row (#2209).
 */
/**
 * Set in the navigation state by a jump out of the navigation. Such a visit
 * brings back the filters left behind but not the search text: a search from
 * days ago made a partner look missing (R-271). Going back from a record is a
 * history step and brings back everything.
 */
export const PALO_FRESH_LIST = 'paloFreshList';

const LAST_STATE_NAME = '__last__';

/**
 * The filter the list opens with.
 *
 * Stored like any other: a name no one can type, whose query names the filter
 * it points at. Pointing rather than copying, so that renaming or deleting
 * that filter does not leave a stale copy behind (#2209).
 */
const DEFAULT_NAME = '__default__';

/**
 * The columns this user arranged for this list.
 *
 * Kept apart from the saved filters on purpose. A saved filter is something a
 * user names and chooses; the column arrangement is not — it is how they like
 * to look at this list, and it should simply still be there, whichever filter
 * they pick. Putting both in one thing is what made "Sichten" hard to grasp.
 */
const COLUMNS_NAME = '__columns__';

/** What a saved filter never carries: the layout, and where one happened to be. */
const LAYOUT_KEYS = ['page', 'limit', 'pageSize', COLUMNS_PARAM, SHOWN_PARAM, ORDER_PARAM];

/** The narrowing part of a query — what a saved filter is made of. */
const filterPart = (query: string): string => {
    const params = new URLSearchParams(query);

    LAYOUT_KEYS.forEach(key => params.delete(key));

    return params.toString();
};

/** The same query with every filter taken out; the search term stays. */
const withoutFilters = (query: string): string => {
    const params = new URLSearchParams(query);

    Array.from(params.keys())
        .filter(key => key.startsWith('filter['))
        .forEach(key => params.delete(key));

    return params.toString();
};

/** The arrangement part of a query — what the column store keeps. */
const columnPart = (query: string): string => keep(query, [COLUMNS_PARAM, SHOWN_PARAM, ORDER_PARAM]);

/**
 * What a saved filter leaves untouched when it is applied.
 *
 * How many rows at a time and which columns are how this user looks at the
 * list; picking "Offene Rechnungen" is not a wish to have that changed too.
 * The page is not among them — a narrowed list starts at its beginning.
 */
const layoutPart = (query: string): string =>
    keep(
        query,
        LAYOUT_KEYS.filter(key => 'page' !== key)
    );

const keep = (query: string, keys: string[]): string => {
    const params = new URLSearchParams(query);
    const kept = new URLSearchParams();

    keys.forEach(key => {
        const value = params.get(key);

        if (null !== value) {
            kept.set(key, value);
        }
    });

    return kept.toString();
};

/**
 * Saved filters, and the column arrangement, both carried in the list's query.
 *
 * Every list control already writes to the query, so a saved filter is the
 * query under a name and needs no model per list. The two are kept apart,
 * though: a saved filter holds only what narrows the list, never the columns.
 *
 * The column arrangement is not something anyone saves — it is kept for this
 * user and this list and put back whenever the list opens, whichever filter is
 * picked. One thing that had to be named and chosen for both jobs is what made
 * "Sichten" hard to grasp (#2209).
 */
export const usePaloListViews = (
    listKey: string
): {
    hiddenColumnKeys: string[];
    shownColumnKeys: string[];
    toggleColumn: (key: string, hiddenByDefault?: boolean) => void;
    /** Puts a column one place to the left or right, and remembers it. */
    moveColumn: (key: string, direction: -1 | 1) => void;
    /** The columns in the order the user arranged them. */
    orderColumns: <T extends { key: string }>(columns: T[]) => T[];
    /** The saved filters, as a menu beside the filter button. */
    savedFiltersMenu: ReactElement;
} => {
    const location = useLocation();
    const navigate = useNavigate();
    const [views, setViews] = useState<SavedView[]>([]);
    const [menu, setMenu] = useState<HTMLElement | null>(null);
    const [naming, setNaming] = useState(false);
    const [draftName, setDraftName] = useState('');
    /** The view whose name is being edited, if any. */
    const [renaming, setRenaming] = useState<SavedView | undefined>(undefined);
    /** The name of the view the list opens with, if the user picked one. */
    const [startView, setStartView] = useState<string | null>(null);
    /** What was last written, so the same state is not stored twice. */
    const lastWritten = useRef<string | undefined>(undefined);
    /**
     * Whether the list has been put back the way its user left it.
     *
     * Until that has happened the address is not their doing — it is whatever
     * a link, a bookmark or the navigation carried in — and nothing of it may
     * be kept (LST-S15).
     */
    const [restored, setRestored] = useState(false);
    /** The column keys in the order the mask currently renders them. */
    const effectiveOrder = useRef<string[]>([]);

    const params = new URLSearchParams(location.search);
    const hiddenColumnKeys = (params.get(COLUMNS_PARAM) ?? '').split(',').filter(Boolean);
    const shownColumnKeys = (params.get(SHOWN_PARAM) ?? '').split(',').filter(Boolean);
    const columnOrder = (params.get(ORDER_PARAM) ?? '').split(',').filter(Boolean);

    const load = useCallback(async () => {
        const response = await request.get(`${ENDPOINT}?listKey=${encodeURIComponent(listKey)}`);
        const stored: SavedView[] = response?.data ?? [];

        const reserved = [LAST_STATE_NAME, DEFAULT_NAME, COLUMNS_NAME];
        const own = stored.filter(view => !reserved.includes(view.name));
        const marker = stored.find(view => DEFAULT_NAME === view.name);
        const startName = marker ? new URLSearchParams(marker.query).get('view') : null;

        setViews(own);
        setStartView(startName);

        return {
            // The filter chosen as the one to open with wins over the state
            // someone merely left behind: that one was picked on purpose.
            filters:
                own.find(view => view.name === startName)?.query ??
                stored.find(view => LAST_STATE_NAME === view.name)?.query,
            columns: stored.find(view => COLUMNS_NAME === view.name)?.query,
        };
    }, [listKey]);

    useEffect(() => {
        let cancelled = false;

        setRestored(false);

        const restore = async () => {
            let stored: { filters?: string; columns?: string } = {};

            try {
                stored = await load();
            } catch {
                // A list nobody could read the kept state for still works. What
                // it must not do is write whatever address it happens to carry
                // over a state it never saw.
            }

            if (cancelled) {
                return;
            }

            const current = location.search.replace(/^\?/, '');
            const next = new URLSearchParams(current);

            // Only an address that says nothing: a link someone was sent, a
            // bookmark or a jump out of the navigation carries its own filters
            // and must win over what was left behind here.
            if (stored.filters && '' === filterPart(current)) {
                const fresh = true === (location.state as Record<string, unknown> | null)?.[PALO_FRESH_LIST];

                new URLSearchParams(filterPart(stored.filters)).forEach((value, key) => {
                    if (!(fresh && 'search' === key)) {
                        next.append(key, value);
                    }
                });
            }

            // The arrangement comes back either way — it is not a filter, and
            // a link that carries none is not a wish to see the columns as
            // they come out of the code.
            if (stored.columns && '' === columnPart(current)) {
                new URLSearchParams(stored.columns).forEach((value, key) => next.set(key, value));
            }

            const search = next.toString();

            // Whatever the list opens with is not something the user just set,
            // so it is never written back. A link someone was sent carries its
            // filters for that visit alone and must not become this user's
            // kept state (LST-S15). This used to be noted only when the
            // address had to be changed, so arriving on a link that already
            // matched the stored columns left it unnoted — and the link's
            // filters were written a moment later and came back on every
            // later visit.
            lastWritten.current = filterPart(search);

            if (search !== current) {
                navigate({ search }, { replace: true });
            }

            setRestored(true);
        };

        restore();

        return () => {
            cancelled = true;
        };
        // Once per list: restoring changes the query, and reading it here would
        // undo the user's next move.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    /** Remembers how the user left the list, a moment after they stop moving. */
    useEffect(() => {
        // Whether this state is worth keeping is decided here, before the wait
        // below — and restoring has to read the server first. Noting the
        // restored state in a ref came too late for a slow answer: the link
        // was already on its way to being kept.
        if (!restored) {
            return;
        }

        const current = location.search.replace(/^\?/, '');
        const next = filterPart(current);

        if (next === lastWritten.current) {
            return;
        }

        const timer = setTimeout(() => {
            // A filter the list refused is not a state worth returning to: it
            // was asked again on the next visit and refused again, under chips
            // that said it was applied (#2231 R-118). The wait above gives the
            // list its moment to refuse before this is written.
            const kept = paloIsRefused(current) ? withoutFilters(next) : next;

            lastWritten.current = next;
            request.post(ENDPOINT, { listKey, name: LAST_STATE_NAME, query: kept });
        }, 800);

        return () => clearTimeout(timer);
    }, [listKey, location.search, restored]);

    /** The arrangement is kept the moment it changes; nobody saves it by hand. */
    const rememberColumns = (search: URLSearchParams): void => {
        request.post(ENDPOINT, { listKey, name: COLUMNS_NAME, query: columnPart(search.toString()) });
    };

    const toggleColumn = (key: string, hiddenByDefault?: boolean) => {
        // A column that starts out hidden is recorded the other way round: the
        // list says nothing about the ones it shows, so without this the
        // switched-on column would be hidden again on the next visit.
        const param = hiddenByDefault ? SHOWN_PARAM : COLUMNS_PARAM;
        const current = hiddenByDefault ? shownColumnKeys : hiddenColumnKeys;
        const next = current.includes(key) ? current.filter(entry => entry !== key) : [...current, key];
        const nextParams = new URLSearchParams(location.search);

        if (next.length > 0) {
            nextParams.set(param, next.join(','));
        } else {
            nextParams.delete(param);
        }

        rememberColumns(nextParams);
        navigate({ search: nextParams.toString() }, { replace: true });
    };

    /**
     * The stored order first, everything else behind it in the order the mask
     * declared. A column that was added to the code after someone arranged
     * their list must still turn up, and it turns up at the end.
     */
    const orderColumns = <T extends { key: string }>(columns: T[]): T[] => {
        const known = columnOrder
            .map(key => columns.find(column => column.key === key))
            .filter((column): column is T => undefined !== column);
        const rest = columns.filter(column => !columnOrder.includes(column.key));
        const ordered = [...known, ...rest];

        // What the mask actually shows, for the move below: only the list
        // knows its columns, and it hands them over on every render anyway.
        effectiveOrder.current = ordered.map(column => column.key);

        return ordered;
    };

    const moveColumn = (key: string, direction: -1 | 1) => {
        const current = effectiveOrder.current;
        const from = current.indexOf(key);
        const to = from + direction;

        if (from < 0 || to < 0 || to >= current.length) {
            return;
        }

        const next = [...current];
        next.splice(to, 0, ...next.splice(from, 1));

        const nextParams = new URLSearchParams(location.search);

        nextParams.set(ORDER_PARAM, next.join(','));
        rememberColumns(nextParams);
        navigate({ search: nextParams.toString() }, { replace: true });
    };

    /**
     * Applies a saved filter, keeping the columns as they are.
     *
     * The two are separate things: picking "Offene Rechnungen" is not a wish
     * to have the columns rearranged as well (#2209).
     */
    const applyView = (view: SavedView) => {
        setMenu(null);

        const next = new URLSearchParams(filterPart(view.query));

        new URLSearchParams(layoutPart(location.search)).forEach((value, key) => next.set(key, value));
        navigate({ search: next.toString() });
    };

    const saveView = async () => {
        const name = draftName.trim();

        if (!name) {
            return;
        }

        // Only what narrows the list; the leading question mark is not part of
        // the query the backend stores.
        await request.post(ENDPOINT, { listKey, name, query: filterPart(location.search) });
        setNaming(false);
        setDraftName('');
        setMenu(null);
        await load();
    };

    /**
     * Renaming is saving the same query under the new name and dropping the old
     * entry: the store knows a view by its name, and a name is the only thing
     * about it a user ever wants to change.
     */
    const renameView = async (view: SavedView, name: string) => {
        const next = name.trim();

        if (!next || next === view.name) {
            setRenaming(undefined);

            return;
        }

        await request.post(ENDPOINT, { listKey, name: next, query: view.query });
        await request.delete(`${ENDPOINT}/${view.id}`);
        setRenaming(undefined);
        await load();
    };

    const deleteView = async (view: SavedView) => {
        await request.delete(`${ENDPOINT}/${view.id}`);
        await load();
    };

    /**
     * Marks a view as the one the list opens with, or takes the mark away.
     *
     * Stored as a view of its own whose query names the chosen one, so that
     * renaming or deleting that view does not leave a stale copy behind — the
     * pointer simply finds nothing and the list falls back to where it was
     * left (#2209).
     */
    const setAsStart = async (view: SavedView) => {
        const next = view.name === startView ? '' : view.name;

        await request.post(ENDPOINT, {
            listKey,
            name: DEFAULT_NAME,
            query: next ? `view=${encodeURIComponent(next)}` : '',
        });
        setStartView(next || null);
    };

    // Which saved filter is showing, answered by comparing what narrows the
    // list — the columns are not part of it, so rearranging them does not take
    // the name off the button.
    const sameFilters = (query: string): boolean =>
        new URLSearchParams(filterPart(query)).toString().split('&').sort().join('&') ===
        new URLSearchParams(filterPart(location.search)).toString().split('&').sort().join('&');
    const showing = views.find(view => sameFilters(view.query));

    const savedFiltersMenu = (
        <>
            <Button
                size="small"
                startIcon={<BookmarkBorderOutlined sx={{ fontSize: 15 }} />}
                onClick={event => setMenu(event.currentTarget)}
                sx={{ color: showing ? tokens.color.accent : undefined }}
            >
                {showing ? showing.name : <FormattedMessage id="app.list.savedFilters" />}
            </Button>
            <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
                {views.length === 0 && !naming && (
                    <MenuItem disabled={true}>
                        <FormattedMessage id="app.list.savedFilters.empty" />
                    </MenuItem>
                )}
                {views.map(view =>
                    renaming?.id === view.id ? (
                        <MenuItem key={view.id} disableRipple={true}>
                            <TextField
                                size="small"
                                autoFocus={true}
                                defaultValue={view.name}
                                onClick={event => event.stopPropagation()}
                                onBlur={event => renameView(view, event.target.value)}
                                onKeyDown={event => {
                                    if ('Enter' === event.key) {
                                        renameView(view, (event.target as HTMLInputElement).value);
                                    }

                                    if ('Escape' === event.key) {
                                        setRenaming(undefined);
                                    }
                                }}
                            />
                        </MenuItem>
                    ) : (
                        <MenuItem key={view.id} onClick={() => applyView(view)}>
                            <ListItemText>{view.name}</ListItemText>
                            {/* The view a list opens with, marked where it is
                                chosen (#2209). */}
                            {view.name === startView ? (
                                <StarOutlined
                                    titleAccess="Wird beim Öffnen angewendet"
                                    sx={{ fontSize: 15, ml: 2, color: tokens.color.warn }}
                                    onClick={event => {
                                        event.stopPropagation();
                                        setAsStart(view);
                                    }}
                                />
                            ) : (
                                <StarBorderOutlined
                                    titleAccess="Beim Öffnen anwenden"
                                    sx={{ fontSize: 15, ml: 2 }}
                                    onClick={event => {
                                        event.stopPropagation();
                                        setAsStart(view);
                                    }}
                                />
                            )}
                            <EditOutlined
                                titleAccess="Umbenennen"
                                sx={{ fontSize: 15, ml: 2 }}
                                onClick={event => {
                                    event.stopPropagation();
                                    setRenaming(view);
                                }}
                            />
                            <DeleteOutline
                                titleAccess="Löschen"
                                sx={{ fontSize: 15, ml: 1 }}
                                onClick={event => {
                                    event.stopPropagation();
                                    deleteView(view);
                                }}
                            />
                        </MenuItem>
                    )
                )}
                <Divider />
                {naming ? (
                    <MenuItem disableRipple={true}>
                        <TextField
                            size="small"
                            autoFocus={true}
                            value={draftName}
                            onChange={event => setDraftName(event.target.value)}
                            onKeyDown={event => {
                                if ('Enter' === event.key) {
                                    saveView();
                                }
                            }}
                        />
                    </MenuItem>
                ) : (
                    <MenuItem onClick={() => setNaming(true)}>
                        <FormattedMessage id="app.list.savedFilters.save" />
                    </MenuItem>
                )}
            </Menu>
        </>
    );

    return { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu };
};
